import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  FiBriefcase, FiPlus, FiSearch, FiFilter, FiCalendar, 
  FiExternalLink, FiEdit, FiTrash2, FiChevronDown,
  FiChevronRight, FiMapPin, FiCheck, FiUser, FiZap, FiInfo
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { jobApi, applicationApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import ExtensionBanner from '../../components/ExtensionBanner';
import LoadingSpinner from '../../components/LoadingSpinner';
import SavedJobsFilterBar from '../../components/jobs/SavedJobsFilterBar';
import PageHeader from '../../components/layout/PageHeader';
import Tooltip from '../../components/Tooltip';
import { pageDescriptions } from '../../data/pageDescriptions';
import { useCache } from '../../contexts/CacheContext';

const JobList = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [expandedJobs, setExpandedJobs] = useState({});
  const [appliedJobs, setAppliedJobs] = useState({});
  const [applications, setApplications] = useState([]);
  const [searchParams] = useSearchParams();
  const [highlightedJobId, setHighlightedJobId] = useState(null);
  const [showRemoveOldJobsModal, setShowRemoveOldJobsModal] = useState(false);
  const [removeOldJobsDaysInput, setRemoveOldJobsDaysInput] = useState('7');
  const [removingOldJobs, setRemovingOldJobs] = useState(false);
  const [filters, setFilters] = useState({
    dateFilter: 'all',
    applicationFilter: 'all'
  });
  const [showReapplyModal, setShowReapplyModal] = useState(false);
  const [jobToReapply, setJobToReapply] = useState(null);
  const [reapplying, setReapplying] = useState(false);

  const { getJobs, getApplications, invalidateJobCache, invalidateApplicationCache } = useCache();

  useEffect(() => {
    fetchJobs();
    fetchApplications();
    
    // Check if there's a highlighted job ID in the URL
    const highlight = searchParams.get('highlight');
    if (highlight) {
      setHighlightedJobId(highlight);
      // Auto-expand the highlighted job
      setExpandedJobs(prev => ({
        ...prev,
        [highlight]: true
      }));
      
      // Clear the highlight after 5 seconds
      const timer = setTimeout(() => {
        setHighlightedJobId(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  const fetchJobs = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const jobs = await getJobs(forceRefresh);
      setJobs(jobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      const applications = await getApplications();
      setApplications(applications);
      
      // Create a mapping of jobId -> application (use String for consistent lookup)
      const appliedJobsMap = {};
      applications.forEach(application => {
        appliedJobsMap[String(application.jobId)] = application;
      });

      setAppliedJobs(appliedJobsMap);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to load applications');
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSortBy(field);
      setSortDirection('desc');
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const toggleJobExpanded = (jobId) => {
    setExpandedJobs({
      ...expandedJobs,
      [jobId]: !expandedJobs[jobId]
    });
  };
  
  const confirmDelete = (job) => {
    setJobToDelete(job);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      await jobApi.deleteJob(jobToDelete.id);
      
      // Update UI optimistically
      setJobs(jobs.filter(job => job.id !== jobToDelete.id));
      
      // Invalidate the job cache
      invalidateJobCache();
      
      toast.success('Job deleted successfully');
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error('Failed to delete job');
    }
  };

  const handleConfirmReapply = async () => {
    if (!jobToReapply) return;
    const applicationId = getApplicationId(jobToReapply.id);
    if (!applicationId) {
      toast.error('Application not found');
      setShowReapplyModal(false);
      setJobToReapply(null);
      return;
    }
    setReapplying(true);
    try {
      await applicationApi.deleteApplication(applicationId);
      invalidateApplicationCache();
      await fetchApplications();
      const jobId = jobToReapply.id;
      setShowReapplyModal(false);
      setJobToReapply(null);
      toast.success('Existing application removed. You can now submit a new application.');
      navigate(`/jobs/${jobId}/apply`);
    } catch (error) {
      console.error('Error removing application:', error);
      toast.error('Failed to remove application');
    } finally {
      setReapplying(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Check if user has applied for a job
  const hasAppliedForJob = (jobId) => {
    return appliedJobs[String(jobId)] !== undefined;
  };

  // Get application ID for a job
  const getApplicationId = (jobId) => {
    return appliedJobs[String(jobId)]?.id;
  };

  // Parse days from input (default 7, min 1) for use in eligibility and confirm
  const getRemoveOldJobsDaysParsed = () => Math.max(1, parseInt(removeOldJobsDaysInput, 10) || 7);

  // Jobs eligible for "remove old unapplied": not applied and older than X days (valid createdAt only)
  const getEligibleJobsToRemove = (days) => {
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
    return jobs.filter((job) => {
      if (appliedJobs[String(job.id)]) return false;
      const createdAt = job.createdAt;
      if (!createdAt) return false;
      const createdMs = new Date(createdAt).getTime();
      if (Number.isNaN(createdMs)) return false;
      return createdMs < cutoff;
    });
  };

  const handleRemoveOldUnappliedJobs = async () => {
    const days = getRemoveOldJobsDaysParsed();
    const eligible = getEligibleJobsToRemove(days);
    if (eligible.length === 0) return;
    setRemovingOldJobs(true);
    const BATCH = 5;
    let removed = 0;
    try {
      for (let i = 0; i < eligible.length; i += BATCH) {
        const batch = eligible.slice(i, i + BATCH);
        await Promise.all(batch.map(job => jobApi.deleteJob(job.id)));
        removed += batch.length;
      }
      invalidateJobCache();
      await fetchJobs(true);
      await fetchApplications();
      setShowRemoveOldJobsModal(false);
      toast.success(`Removed ${removed} job${removed !== 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error removing old jobs:', error);
      toast.error('Failed to remove some jobs');
    } finally {
      setRemovingOldJobs(false);
    }
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
  };

  // Filter by date added and application status, then search, then sort
  const filteredAndSortedJobs = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    return [...jobs]
      .filter((job) => {
        if (filters.dateFilter !== 'all') {
          const createdMs = job.createdAt ? new Date(job.createdAt).getTime() : 0;
          if (Number.isNaN(createdMs)) return false;
          let daysToFilter;
          switch (filters.dateFilter) {
            case '7days': daysToFilter = 7; break;
            case '30days': daysToFilter = 30; break;
            case '90days': daysToFilter = 90; break;
            default: daysToFilter = 0;
          }
          if (daysToFilter > 0) {
            const cutoff = Date.now() - daysToFilter * 24 * 60 * 60 * 1000;
            if (createdMs < cutoff) return false;
          }
        }
        if (filters.applicationFilter === 'applied' && !appliedJobs[String(job.id)]) return false;
        if (filters.applicationFilter === 'not_applied' && appliedJobs[String(job.id)]) return false;
        return true;
      })
      .filter(job =>
        String(job.title ?? '').toLowerCase().includes(lowerSearch) ||
        String(job.company ?? '').toLowerCase().includes(lowerSearch)
      )
      .sort((a, b) => {
        let comparison = 0;
        if (sortBy === 'title') {
          comparison = String(a.title ?? '').localeCompare(String(b.title ?? ''));
        } else if (sortBy === 'company') {
          comparison = String(a.company ?? '').localeCompare(String(b.company ?? ''));
        } else if (sortBy === 'createdAt') {
          comparison = new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        }
        return sortDirection === 'asc' ? comparison : -comparison;
      });
  }, [jobs, filters, appliedJobs, searchTerm, sortBy, sortDirection]);

  if (loading) {
    return <LoadingSpinner message="Loading job opportunities..." />;
  }

  return (
    <div className="space-y-6">
      {/* Extension Banner */}
      <ExtensionBanner />
      
      <PageHeader
        title={pageDescriptions['/jobs/saved'].title}
        description={pageDescriptions['/jobs/saved'].description}
        whatYouCanDo={pageDescriptions['/jobs/saved'].whatYouCanDo}
        whatYouShouldDo={pageDescriptions['/jobs/saved'].whatYouShouldDo}
        actions={
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={() => {
                setRemoveOldJobsDaysInput('7');
                setShowRemoveOldJobsModal(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <FiTrash2 className="-ml-1 mr-2 h-4 w-4" />
              Remove old jobs
              <span
                className="ml-1.5 inline-flex text-gray-400 hover:text-gray-600"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                role="img"
                aria-label="Info"
              >
                <Tooltip
                  position="bottom"
                  wrap
                  extraWide
                  text="Remove saved jobs you have not applied to that are older than the number of days below."
                >
                  <FiInfo className="h-4 w-4" />
                </Tooltip>
              </span>
            </button>
            <Link
              to="/jobs/new"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <FiPlus className="-ml-1 mr-2 h-4 w-4" />
              Add New Job
            </Link>
          </div>
        }
      />

      <SavedJobsFilterBar onApplyFilters={handleApplyFilters} />

      {/* Remind user to set auto-cleanup in Settings */}
      {currentUser && !currentUser.auto_cleanup_enabled && (
        <div className="rounded-md bg-amber-50 border border-amber-200 p-4 flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm text-amber-800">
            You can automatically remove old saved jobs and mark old applications as Rejected each time you log in. Set it up in Settings.
          </p>
          <Link
            to="/settings"
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-amber-800 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
          >
            Open Settings
          </Link>
        </div>
      )}

      {/* Search and Filters */}
      <div className="card p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="h-12 text-base focus:ring-primary-500 focus:border-primary-500 block w-full pl-10 border-gray-300 rounded-md"
              placeholder="Search jobs by title or company..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="flex flex-row gap-4">
            <div className="w-full md:w-auto">
              <label htmlFor="sort-by" className="sr-only">Sort by</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiFilter className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  id="sort-by"
                  name="sort-by"
                  className="h-12 text-base focus:ring-primary-500 focus:border-primary-500 block w-full pl-10 border-gray-300 rounded-md"
                  value={sortBy}
                  onChange={(e) => handleSort(e.target.value)}
                >
                  <option value="createdAt">Date Added</option>
                  <option value="title">Job Title</option>
                  <option value="company">Company</option>
                </select>
              </div>
            </div>
            <button
              type="button"
              aria-label={sortDirection === 'asc' ? 'Sort ascending' : 'Sort descending'}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
            >
              {sortDirection === 'asc' ? 'Ascending' : 'Descending'}
            </button>
          </div>
        </div>
      </div>

      {/* Jobs List */}
      <div className="card overflow-hidden">
        {filteredAndSortedJobs.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {filteredAndSortedJobs.map((job) => (
              <React.Fragment key={job.id}>
                <li className={highlightedJobId === job.id ? "bg-yellow-50 transition-colors duration-1000" : ""}>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="sm:flex sm:items-center">
                        <div className="flex-shrink-0">
                          {/* Company Logo */}
                          {job.logo && job.logo.trim() ? (
                            <img 
                              src={job.logo} 
                              alt={`${job.company} logo`}
                              className="h-12 w-12 rounded-md object-cover border border-gray-200"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                const fallback = e.target.nextElementSibling;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className={`h-12 w-12 rounded-md bg-gradient-to-br from-blue-500 to-primary-600 flex items-center justify-center text-white font-bold text-lg border border-gray-200 ${
                              job.logo && job.logo.trim() ? 'hidden' : 'flex'
                            }`}
                          >
                            {(typeof job.company === 'string' && job.company) ? job.company.charAt(0).toUpperCase() : 'C'}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center">
                            <h3 
                              className="text-lg font-medium text-primary-600 truncate"
                              title={job.title}
                            >
                              {job.title}
                            </h3>
                            {/* Application Status Badge */}
                            {hasAppliedForJob(job.id) && (
                              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <FiCheck className="mr-1 h-3 w-3" />
                                Applied
                              </span>
                            )}
                            {/* Mentor Assignment Badge */}
                            {job.assigned_by_mentor && (
                              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                <FiUser className="mr-1 h-3 w-3" />
                                Assigned by {job.mentor_name}
                              </span>
                            )}
                            {/* Sponsor Badge */}
                            {job.sponsor && (
                              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Sponsors Visa
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex items-center">
                            <div className="text-sm text-gray-700 font-medium">
                              {job.company}
                            </div>
                            {job.sourceUrl && (
                              <a
                                href={job.sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-2 inline-flex items-center text-xs text-gray-500 hover:text-primary-500"
                              >
                                <FiExternalLink className="h-3 w-3 mr-1" />
                                Source
                              </a>
                            )}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-500">
                            <FiCalendar className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            <span>Added on {formatDate(job.createdAt)}</span>
                            
                            {job.location && (
                              <div className="flex items-center ml-3">
                                <FiMapPin className="flex-shrink-0 mr-1 h-4 w-4 text-gray-400" />
                                <span>{job.location}</span>
                              </div>
                            )}
                            
                            {job.skills && Array.isArray(job.skills) && job.skills.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1 sm:mt-0 sm:ml-3">
                                {job.skills.slice(0, 3).map((skill, index) => (
                                  <span 
                                    key={index}
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800"
                                  >
                                    {skill}
                                  </span>
                                ))}
                                {job.skills.length > 3 && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                    +{job.skills.length - 3} more
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 flex-shrink-0 sm:mt-0 sm:ml-5">
                        <div className="flex space-x-2">
                          <button
                            type="button"
                            aria-label={expandedJobs[job.id] ? 'Hide job details' : 'Show job details'}
                            aria-expanded={expandedJobs[job.id] ? 'true' : 'false'}
                            onClick={() => toggleJobExpanded(job.id)}
                            className="inline-flex items-center px-2 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            {expandedJobs[job.id] ? (
                              <>
                                <FiChevronDown className="mr-1 h-4 w-4" />
                                Hide Details
                              </>
                            ) : (
                              <>
                                <FiChevronRight className="mr-1 h-4 w-4" />
                                Show Details
                              </>
                            )}
                          </button>
                          
                          {/* Conditional Apply / View Application / Re-apply */}
                          {hasAppliedForJob(job.id) ? (
                            <>
                              <Link
                                to={`/applications`}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              >
                                <FiCheck className="-ml-0.5 mr-1 h-4 w-4" />
                                View Application
                              </Link>
                              <button
                                type="button"
                                aria-label="Re-apply for this job"
                                onClick={() => {
                                  setJobToReapply(job);
                                  setShowReapplyModal(true);
                                }}
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                              >
                                Re-apply
                              </button>
                            </>
                          ) : (
                            <Link
                              to={`/jobs/${job.id}/apply`}
                              aria-label="Apply for this job"
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                              Apply
                            </Link>
                          )}
                          
                          <Link
                            to={`/jobs/${job.id}/edit`}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            aria-label="Edit job"
                          >
                            <FiEdit className="-ml-0.5 mr-1 h-4 w-4" />
                            Edit
                          </Link>
                          <button
                            type="button"
                            aria-label="Delete job"
                            onClick={() => confirmDelete(job)}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <FiTrash2 className="-ml-0.5 mr-1 h-4 w-4" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
                
                {/* Expandable job details */}
                {expandedJobs[job.id] && (
                  <li className="bg-gray-50 border-t border-gray-200">
                    <div className="px-4 py-4 sm:px-6">
                      {/* Application Status Section - New! */}
                      {hasAppliedForJob(job.id) && (
                        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                          <div className="flex items-center">
                            <FiCheck className="h-5 w-5 text-green-500 mr-2" />
                            <span className="text-sm font-medium text-green-800">You have applied for this position</span>
                          </div>
                          <p className="text-xs text-green-700 mt-1">
                            Application status: {appliedJobs[String(job.id)]?.status || 'Applied'}
                          </p>
                          <div className="mt-2">
                            <Link
                              to={`/applications`}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200"
                            >
                              View application details
                            </Link>
                          </div>
                        </div>
                      )}
                      
                      {/* Job Info Section */}
                      <div className="mb-4 grid grid-cols-2 gap-4">
                        {job.location && (
                          <div>
                            <span className="text-sm font-medium text-gray-700">Location:</span>
                            <span className="ml-2 text-sm text-gray-600">{job.location}</span>
                          </div>
                        )}
                        
                        {job.employment_type && (
                          <div>
                            <span className="text-sm font-medium text-gray-700">Type:</span>
                            <span className="ml-2 text-sm text-gray-600">{job.employment_type}</span>
                          </div>
                        )}
                        
                        {job.salary_range && (
                          <div>
                            <span className="text-sm font-medium text-gray-700">Salary Range:</span>
                            <span className="ml-2 text-sm text-gray-600">{job.salary_range}</span>
                          </div>
                        )}
                        {job.sourceUrl && (
                          <div className="col-span-2">
                            <span className="text-sm font-medium text-gray-700">Job post:</span>
                            <span className="ml-2 text-sm text-gray-600">
                              <a
                                href={job.sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 hover:text-primary-800"
                              >
                                View job posting
                              </a>
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Job skills & requirements - if available */}
                      {job.skills && job.skills.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Skills</h4>
                          <div className="flex flex-wrap gap-2">
                            {job.skills.map((skill, index) => (
                              <span 
                                key={index}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Job responsibilities - if available */}
                      {job.responsibilities && job.responsibilities.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Responsibilities</h4>
                          <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
                            {job.responsibilities.map((resp, index) => (
                              <li key={index}>{resp}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Job requirements - if available */}
                      {job.requirements && job.requirements.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Requirements</h4>
                          <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
                            {job.requirements.map((req, index) => (
                              <li key={index}>{req}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </li>
                )}
              </React.Fragment>
            ))}
          </ul>
        ) : (
          <div className="py-12 text-center">
            <FiBriefcase className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {filters.dateFilter !== 'all' || filters.applicationFilter !== 'all'
                ? 'No jobs match your current filters.'
                : searchTerm
                  ? 'Try a different search term'
                  : 'Get started by adding a job opportunity'}
            </p>
            {(filters.dateFilter !== 'all' || filters.applicationFilter !== 'all') ? (
              <p className="mt-2 text-sm text-primary-600">Use &quot;Clear filters&quot; above to see all jobs.</p>
            ) : !searchTerm && (
              <div className="mt-6">
                <Link
                  to="/jobs/new"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <FiPlus className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                  Add New Job
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && jobToDelete && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <FiTrash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Job</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete this job opportunity? This action cannot be undone.
                      </p>
                      <p className="text-sm font-medium text-gray-900 mt-2">
                        {jobToDelete.title} at {jobToDelete.company}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  aria-label="Confirm delete job"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleDelete}
                >
                  Delete
                </button>
                <button
                  type="button"
                  aria-label="Cancel delete"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Re-apply confirmation modal */}
      {showReapplyModal && jobToReapply && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 sm:mx-0 sm:h-10 sm:w-10">
                    <FiBriefcase className="h-6 w-6 text-amber-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Re-apply for this job</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Re-applying will delete your existing application for this job. You will then be able to submit a new application. Continue?
                      </p>
                      <p className="text-sm font-medium text-gray-900 mt-2">
                        {jobToReapply.title} at {jobToReapply.company}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  aria-label="Continue re-apply"
                  disabled={reapplying}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleConfirmReapply}
                >
                  {reapplying ? 'Removing…' : 'Continue'}
                </button>
                <button
                  type="button"
                  aria-label="Cancel re-apply"
                  disabled={reapplying}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  onClick={() => {
                    setShowReapplyModal(false);
                    setJobToReapply(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove old unapplied jobs modal */}
      {showRemoveOldJobsModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 sm:mx-0 sm:h-10 sm:w-10">
                    <FiZap className="h-6 w-6 text-amber-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Remove old unapplied jobs</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      Remove saved jobs you have not applied to that are older than the number of days below.
                    </p>
                    <div className="mt-4">
                      <label htmlFor="remove-old-days" className="block text-sm font-medium text-gray-700">
                        Older than (days)
                      </label>
                      <input
                        id="remove-old-days"
                        type="number"
                        min={1}
                        value={removeOldJobsDaysInput}
                        onChange={(e) => setRemoveOldJobsDaysInput(e.target.value)}
                        className="mt-1 block w-24 rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                    </div>
                    <p className="mt-3 text-sm text-gray-600">
                      {getEligibleJobsToRemove(getRemoveOldJobsDaysParsed()).length === 0
                        ? 'No jobs match.'
                        : `${getEligibleJobsToRemove(getRemoveOldJobsDaysParsed()).length} job(s) will be removed.`}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  aria-label="Confirm remove old unapplied jobs"
                  disabled={getEligibleJobsToRemove(getRemoveOldJobsDaysParsed()).length === 0 || removingOldJobs}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleRemoveOldUnappliedJobs}
                >
                  {removingOldJobs ? 'Removing…' : 'Confirm'}
                </button>
                <button
                  type="button"
                  aria-label="Cancel remove old jobs"
                  disabled={removingOldJobs}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  onClick={() => setShowRemoveOldJobsModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobList;